import React, { useCallback, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import './BiasIndicatorManager.css';

// Icons as inline SVGs for crisp rendering
const Icon: React.FC<{ children: React.ReactNode; size?: number; className?: string }> = ({ 
  children, 
  size = 16,
  className = ''
}) => (
  <span className={`icon ${className}`} style={{ display: 'inline-flex', width: size, height: size, flexShrink: 0 }}>
    {children}
  </span>
);

const Icons = {
  Scale: ({ size = 16 }: { size?: number }) => (
    <Icon size={size}>
      <svg width="100%" height="100%" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 3v18" /><path d="M5 6l7-3 7 3" /><path d="M3 10l2-1v5l-2 1" /><path d="M21 10l-2-1v5l2 1" />
        <path d="M5 15l2-1" /><path d="M17 15l2-1" /><path d="M3 19h4" /><path d="M17 19h4" />
      </svg>
    </Icon>
  ),
  Search: ({ size = 16 }: { size?: number }) => (
    <Icon size={size}>
      <svg width="100%" height="100%" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" />
      </svg>
    </Icon>
  ),
  Plus: ({ size = 16 }: { size?: number }) => (
    <Icon size={size}>
      <svg width="100%" height="100%" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 5v14" /><path d="M5 12h14" />
      </svg>
    </Icon>
  ),
  Refresh: ({ size = 16 }: { size?: number }) => (
    <Icon size={size}>
      <svg width="100%" height="100%" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
        <path d="M21 3v5h-5" /><path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
        <path d="M8 16H3v5" />
      </svg>
    </Icon>
  ),
  Edit: ({ size = 16 }: { size?: number }) => (
    <Icon size={size}>
      <svg width="100%" height="100%" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
      </svg>
    </Icon>
  ),
  Trash: ({ size = 16 }: { size?: number }) => (
    <Icon size={size}>
      <svg width="100%" height="100%" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 6h18" /><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
        <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
      </svg>
    </Icon>
  ),
  Users: ({ size = 16 }: { size?: number }) => (
    <Icon size={size}>
      <svg width="100%" height="100%" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" />
        <path d="M22 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>
    </Icon>
  ),
  AlertCircle: ({ size = 16 }: { size?: number }) => (
    <Icon size={size}>
      <svg width="100%" height="100%" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
      </svg>
    </Icon>
  ),
  Check: ({ size = 16 }: { size?: number }) => (
    <Icon size={size}>
      <svg width="100%" height="100%" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="20 6 9 17 4 12" />
      </svg>
    </Icon>
  ),
  X: ({ size = 16 }: { size?: number }) => (
    <Icon size={size}>
      <svg width="100%" height="100%" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M18 6 6 18" /><path d="m6 6 12 12" />
      </svg>
    </Icon>
  ),
  Hash: ({ size = 16 }: { size?: number }) => (
    <Icon size={size}>
      <svg width="100%" height="100%" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="4" y1="9" x2="20" y2="9" /><line x1="4" y1="15" x2="20" y2="15" />
        <line x1="10" y1="3" x2="8" y2="21" /><line x1="16" y1="3" x2="14" y2="21" />
      </svg>
    </Icon>
  ),
  Inbox: ({ size = 16 }: { size?: number }) => (
    <Icon size={size}>
      <svg width="100%" height="100%" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="22 12 16 12 14 15 10 15 8 12 2 12" />
        <path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
      </svg>
    </Icon>
  ),
};

interface BiasIndicatorConfiguration {
  bias_indicator_configuration_id: number;
  enabled: boolean;
  display_name: string;
  indicator_type: string;
  arguments?: Record<string, unknown>;
  persona_group_ids: number[];
}

interface BiasIndicatorFormData {
  enabled: boolean;
  display_name: string;
  indicator_type: string;
  arguments?: Record<string, unknown>;
  persona_group_ids: number[];
}

interface PersonaGroupSummary {
  group_id: number;
  name: string;
}

interface BiasIndicatorModalProps {
  config?: BiasIndicatorConfiguration;
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: BiasIndicatorFormData) => void;
  isLoading: boolean;
  personaGroups: PersonaGroupSummary[];
}

const BiasIndicatorModal: React.FC<BiasIndicatorModalProps> = ({
  config,
  isOpen,
  onClose,
  onSave,
  isLoading,
  personaGroups,
}) => {
  const [formData, setFormData] = useState<BiasIndicatorFormData>(() => ({
    enabled: config?.enabled ?? true,
    display_name: config?.display_name || '',
    indicator_type: config?.indicator_type || '',
    persona_group_ids: config?.persona_group_ids || [],
  }));
  const [argumentsText, setArgumentsText] = useState(() => (
    config?.arguments ? JSON.stringify(config.arguments, null, 2) : ''
  ));
  const [argumentsError, setArgumentsError] = useState<string | null>(null);
  const [groupSearchQuery, setGroupSearchQuery] = useState('');

  React.useEffect(() => {
    if (!isOpen) return;
    setFormData({
      enabled: config?.enabled ?? true,
      display_name: config?.display_name || '',
      indicator_type: config?.indicator_type || '',
      persona_group_ids: config?.persona_group_ids || [],
    });
    setArgumentsText(config?.arguments ? JSON.stringify(config.arguments, null, 2) : '');
    setArgumentsError(null);
    setGroupSearchQuery('');
  }, [config, isOpen]);

  React.useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, onClose]);

  const filteredGroups = useMemo(() => {
    if (!groupSearchQuery.trim()) return personaGroups;
    const query = groupSearchQuery.toLowerCase();
    return personaGroups.filter(group => 
      group.name.toLowerCase().includes(query)
    );
  }, [personaGroups, groupSearchQuery]);

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    let parsedArguments: Record<string, unknown> | undefined;

    if (argumentsText.trim()) {
      try {
        const parsedValue = JSON.parse(argumentsText);
        if (!parsedValue || Array.isArray(parsedValue) || typeof parsedValue !== 'object') {
          setArgumentsError('Arguments must be a JSON object.');
          return;
        }
        parsedArguments = parsedValue as Record<string, unknown>;
      } catch {
        setArgumentsError('Arguments must be valid JSON.');
        return;
      }
    }

    onSave({
      ...formData,
      arguments: parsedArguments,
    });
  };

  const togglePersonaGroup = (groupId: number) => {
    setFormData(prev => ({
      ...prev,
      persona_group_ids: prev.persona_group_ids.includes(groupId)
        ? prev.persona_group_ids.filter(id => id !== groupId)
        : [...prev.persona_group_ids, groupId],
    }));
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content bias-modal" onClick={event => event.stopPropagation()}>
        <div className="modal-header">
          <h2>{config ? 'Edit Bias Indicator' : 'Create Bias Indicator'}</h2>
          <button className="bias-action-btn" onClick={onClose}>
            <Icons.X />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="bias-form">
          <div className="bias-form-row">
            <div className="bias-form-group">
              <label className="bias-form-label">
                Display Name <span className="required">*</span>
              </label>
              <input
                type="text"
                className="bias-form-input"
                value={formData.display_name}
                onChange={(event) => setFormData(prev => ({ ...prev, display_name: event.target.value }))}
                required
                placeholder="e.g., Sentiment Analysis"
              />
            </div>

            <div className="bias-form-group">
              <label className="bias-form-label">
                Indicator Type <span className="required">*</span>
              </label>
              <input
                type="text"
                className="bias-form-input"
                value={formData.indicator_type}
                onChange={(event) => setFormData(prev => ({ ...prev, indicator_type: event.target.value }))}
                required
                placeholder="e.g., sentiment_shift"
              />
            </div>
          </div>

          <div className="bias-form-group full-width">
            <label className="bias-form-label">Arguments (JSON)</label>
            <textarea
              className="bias-form-textarea"
              value={argumentsText}
              onChange={(event) => {
                setArgumentsText(event.target.value);
                if (argumentsError) setArgumentsError(null);
              }}
              placeholder='{"threshold": 0.25, "window_days": 30}'
            />
            <span className="bias-form-help">Optional configuration parameters for this indicator.</span>
            {argumentsError && (
              <span className="bias-form-error">
                <Icons.AlertCircle size={14} /> {argumentsError}
              </span>
            )}
          </div>

          <div className="bias-toggle-group">
            <div className="bias-toggle-label">
              <span className="bias-toggle-title">Enable Indicator</span>
              <span className="bias-toggle-desc">Process data with this indicator configuration</span>
            </div>
            <div 
              className={`bias-toggle ${formData.enabled ? 'active' : ''}`}
              onClick={() => setFormData(prev => ({ ...prev, enabled: !prev.enabled }))}
            />
          </div>

          <div className="bias-groups-section">
            <div className="bias-groups-header">
              <label className="bias-form-label">
                <Icons.Users size={14} /> Persona Groups
              </label>
              <span className="bias-selected-count">
                {formData.persona_group_ids.length} selected
              </span>
            </div>
            
            <div className="bias-groups-search">
              <span className="bias-groups-search-icon"><Icons.Search size={16} /></span>
              <input
                type="text"
                className="bias-groups-search-input"
                placeholder="Search groups..."
                value={groupSearchQuery}
                onChange={(e) => setGroupSearchQuery(e.target.value)}
              />
            </div>

            <div className="bias-groups-list">
              {filteredGroups.length > 0 ? (
                filteredGroups.map(group => {
                  const isSelected = formData.persona_group_ids.includes(group.group_id);
                  return (
                    <div
                      key={group.group_id}
                      className={`bias-group-item ${isSelected ? 'selected' : ''}`}
                      onClick={() => togglePersonaGroup(group.group_id)}
                    >
                      <div className="bias-group-checkbox">
                        <Icons.Check size={12} />
                      </div>
                      <span className="bias-group-name">{group.name}</span>
                    </div>
                  );
                })
              ) : (
                <div className="bias-groups-empty">
                  {groupSearchQuery ? 'No groups match your search' : 'No persona groups available'}
                </div>
              )}
            </div>
          </div>

          <div className="bias-modal-footer">
            <button type="button" onClick={onClose} className="btn btn-secondary">
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={isLoading}>
              {isLoading ? 'Saving...' : (config ? 'Update Configuration' : 'Create Configuration')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};

const BiasIndicatorManager: React.FC = () => {
  const queryClient = useQueryClient();

  const [selectedConfig, setSelectedConfig] = useState<BiasIndicatorConfiguration | undefined>();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const { data: configurations = [], isLoading, error, refetch } = useQuery<BiasIndicatorConfiguration[]>({
    queryKey: ['bias-indicator-configurations'],
    queryFn: async () => {
      const response = await fetch('/api/bias-indicator');
      if (!response.ok) throw new Error('Failed to fetch bias indicator configurations');
      return response.json();
    },
    refetchInterval: 30000,
  });

  const { data: personaGroups = [] } = useQuery<PersonaGroupSummary[]>({
    queryKey: ['persona-groups'],
    queryFn: async () => {
      const response = await fetch('/api/persona-groups');
      if (!response.ok) throw new Error('Failed to fetch persona groups');
      return response.json();
    },
  });

  const filteredConfigurations = useMemo(() => {
    if (!searchQuery.trim()) return configurations;
    const query = searchQuery.toLowerCase();
    return configurations.filter(config =>
      config.display_name.toLowerCase().includes(query) ||
      config.indicator_type.toLowerCase().includes(query)
    );
  }, [configurations, searchQuery]);

  const stats = useMemo(() => ({
    total: configurations.length,
    enabled: configurations.filter(c => c.enabled).length,
    disabled: configurations.filter(c => !c.enabled).length,
  }), [configurations]);

  const createConfigMutation = useMutation({
    mutationFn: async (configData: BiasIndicatorFormData) => {
      const response = await fetch('/api/bias-indicator', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(configData),
      });
      if (!response.ok) {
        const errorResponse = await response.json();
        throw new Error(errorResponse.detail || 'Failed to create bias indicator configuration');
      }
      return response.json();
    },
    onSuccess: () => {
      setActionError(null);
      setIsModalOpen(false);
      setSelectedConfig(undefined);
      queryClient.invalidateQueries({ queryKey: ['bias-indicator-configurations'] });
    },
    onError: (error: Error) => setActionError(error.message),
  });

  const updateConfigMutation = useMutation({
    mutationFn: async ({ configId, configData }: { configId: number; configData: BiasIndicatorFormData }) => {
      const response = await fetch(`/api/bias-indicator/${configId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(configData),
      });
      if (!response.ok) {
        const errorResponse = await response.json();
        throw new Error(errorResponse.detail || 'Failed to update bias indicator configuration');
      }
      return response.json();
    },
    onSuccess: () => {
      setActionError(null);
      setIsModalOpen(false);
      setSelectedConfig(undefined);
      queryClient.invalidateQueries({ queryKey: ['bias-indicator-configurations'] });
    },
    onError: (error: Error) => setActionError(error.message),
  });

  const deleteConfigMutation = useMutation({
    mutationFn: async (configId: number) => {
      const response = await fetch(`/api/bias-indicator/${configId}`, {
        method: 'DELETE',
      });
      if (!response.ok) {
        const errorResponse = await response.json();
        throw new Error(errorResponse.detail || 'Failed to delete bias indicator configuration');
      }
      return response.json();
    },
    onSuccess: () => {
      setActionError(null);
      queryClient.invalidateQueries({ queryKey: ['bias-indicator-configurations'] });
    },
    onError: (error: Error) => setActionError(error.message),
  });

  const handleCreateConfig = useCallback(() => {
    setSelectedConfig(undefined);
    setIsModalOpen(true);
    setActionError(null);
  }, []);

  const handleEditConfig = useCallback((config: BiasIndicatorConfiguration) => {
    setSelectedConfig(config);
    setIsModalOpen(true);
    setActionError(null);
  }, []);

  const handleDeleteConfig = useCallback((config: BiasIndicatorConfiguration) => {
    if (window.confirm(`Delete bias indicator "${config.display_name}"? This action cannot be undone.`)) {
      deleteConfigMutation.mutate(config.bias_indicator_configuration_id);
    }
  }, [deleteConfigMutation]);

  const handleSaveConfig = useCallback((configData: BiasIndicatorFormData) => {
    if (selectedConfig) {
      updateConfigMutation.mutate({
        configId: selectedConfig.bias_indicator_configuration_id,
        configData,
      });
    } else {
      createConfigMutation.mutate(configData);
    }
  }, [selectedConfig, createConfigMutation, updateConfigMutation]);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setSelectedConfig(undefined);
    setActionError(null);
  }, []);

  const resolveGroupNames = (groupIds: number[], maxDisplay = 3) => {
    if (!groupIds.length) return [];
    return groupIds.map(id => personaGroups.find(group => group.group_id === id)?.name || `ID: ${id}`);
  };

  if (isLoading) {
    return (
      <div className="bias-indicator-manager">
        <div className="bias-loading">
          <div className="bias-loading-spinner" />
          <span>Loading configurations...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bias-indicator-manager">
        <div className="bias-error-banner">
          <div className="bias-error-content">
            <Icons.AlertCircle size={20} />
            <span>Error loading configurations: {(error as Error).message}</span>
          </div>
          <button onClick={() => refetch()} className="btn btn-secondary">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bias-indicator-manager">
      <div className="bias-header">
        <div className="bias-header-left">
          <h1>
            <Icons.Scale size={28} />
            Bias Indicator Configurations
          </h1>
          <span className="bias-header-subtitle">
            Configure and manage bias detection indicators for persona groups
          </span>
        </div>
        <div className="bias-header-actions">
          <button onClick={() => refetch()} className="btn btn-secondary">
            <Icons.Refresh /> Refresh
          </button>
          <button onClick={handleCreateConfig} className="btn btn-primary">
            <Icons.Plus /> Add Configuration
          </button>
        </div>
      </div>

      <div className="bias-stats-bar">
        <div className="bias-stat">
          <span className="bias-stat-value">{stats.total}</span>
          <span className="bias-stat-label">Total</span>
        </div>
        <div className="bias-stat-divider" />
        <div className="bias-stat">
          <span className="bias-stat-value" style={{ color: 'var(--accent-success)' }}>{stats.enabled}</span>
          <span className="bias-stat-label">Enabled</span>
        </div>
        <div className="bias-stat-divider" />
        <div className="bias-stat">
          <span className="bias-stat-value" style={{ color: 'var(--text-muted)' }}>{stats.disabled}</span>
          <span className="bias-stat-label">Disabled</span>
        </div>
      </div>

      {actionError && (
        <div className="bias-error-banner">
          <div className="bias-error-content">
            <Icons.AlertCircle size={20} />
            <span>{actionError}</span>
          </div>
          <button className="bias-error-dismiss" onClick={() => setActionError(null)}>
            <Icons.X size={16} />
          </button>
        </div>
      )}

      <div className="bias-search-container">
        <span className="bias-search-icon"><Icons.Search size={18} /></span>
        <input
          type="text"
          className="bias-search-input"
          placeholder="Search configurations..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      <div className="bias-config-grid">
        {filteredConfigurations.map(config => {
          const groupNames = resolveGroupNames(config.persona_group_ids);
          const displayGroups = groupNames.slice(0, 3);
          const moreCount = groupNames.length - 3;

          return (
            <div 
              key={config.bias_indicator_configuration_id} 
              className={`bias-config-card ${!config.enabled ? 'disabled' : ''}`}
            >
              <div className="bias-config-header">
                <div className="bias-config-title-section">
                  <h3 className="bias-config-title">{config.display_name}</h3>
                  <span className="bias-config-type">{config.indicator_type}</span>
                </div>
                <div className="bias-config-actions">
                  <button
                    onClick={() => handleEditConfig(config)}
                    className="bias-action-btn"
                    title="Edit configuration"
                  >
                    <Icons.Edit />
                  </button>
                  <button
                    onClick={() => handleDeleteConfig(config)}
                    className="bias-action-btn danger"
                    title="Delete configuration"
                  >
                    <Icons.Trash />
                  </button>
                </div>
              </div>

              <div className="bias-config-content">
                <span className={`bias-status-badge ${config.enabled ? 'enabled' : 'disabled'}`}>
                  <span className="bias-status-dot" />
                  {config.enabled ? 'Enabled' : 'Disabled'}
                </span>

                <div className="bias-config-groups">
                  {displayGroups.length > 0 ? (
                    <>
                      {displayGroups.map((name, idx) => (
                        <span key={idx} className="bias-group-tag">
                          <Icons.Users size={12} /> {name}
                        </span>
                      ))}
                      {moreCount > 0 && (
                        <span className="bias-group-tag bias-group-more">+{moreCount} more</span>
                      )}
                    </>
                  ) : (
                    <span className="bias-group-tag bias-group-more">No groups assigned</span>
                  )}
                </div>

                <div className="bias-config-meta">
                  <span className="bias-meta-item">
                    <Icons.Hash size={14} /> ID: {config.bias_indicator_configuration_id}
                  </span>
                  <span className="bias-meta-item">
                    <Icons.Users size={14} /> {config.persona_group_ids.length} groups
                  </span>
                </div>
              </div>
            </div>
          );
        })}

        {filteredConfigurations.length === 0 && (
          <div className="bias-empty-state">
            <Icons.Inbox size={64} />
            <h3>{searchQuery ? 'No matching configurations' : 'No configurations yet'}</h3>
            <p>
              {searchQuery 
                ? 'Try adjusting your search query'
                : 'Create your first bias indicator configuration to start monitoring bias patterns.'}
            </p>
            {!searchQuery && (
              <button onClick={handleCreateConfig} className="btn btn-primary">
                <Icons.Plus /> Create Configuration
              </button>
            )}
          </div>
        )}
      </div>

      <BiasIndicatorModal
        config={selectedConfig}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSave={handleSaveConfig}
        isLoading={createConfigMutation.isPending || updateConfigMutation.isPending}
        personaGroups={personaGroups}
      />
    </div>
  );
};

export default BiasIndicatorManager;
